import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch} from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Permissions from "src/components/Permissions";
import { ISidebarCardItem } from "src/components/SidebarCard/interface";
import Audit from "src/components/Audit";
import Tabs from "src/components/Tabs";
import DeleteModal from "src/components/DeleteModal";
import DateTimePicker from "src/components/DateTimePicker";
import ModalSide from "src/components/Modal/styles";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useAuth } from "src/context/Auth/context";
import Select from "src/components/Select";
import LovCoins from "src/components/Lov/Coins";
import { IBudgetPlanModal, IOftba001Form, IRefBudgetPlanModals } from "./interface";
import {deleteSchema, addAndChangeSchema} from "./schemas";
import AdditionalPermissions from "./additionalPermissions";

const ModalsBudgetPlanAddChangeDelete: React.ForwardRefRenderFunction<IRefBudgetPlanModals, IBudgetPlanModal> = ({
	onCallbackAdd,
	onCallbackDelete,
	onCallbackUpdate
},ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {setLoading} = useLoaderBackdrop();
	const { company, user } = useAuth();

	const [budgetPlanSelected, setBudgetPlanSelected] = useState<IOftba001Form>();
	const [budgetPlanDelete, setBudgetPlanDelete] = useState<ISidebarCardItem>();

	const monthOptions = [{
		label: translate("Janeiro"),
		value: "1"
	},{
		label: translate("Fevereiro"),
		value: "2"
	},{
		label: translate("Março"),
		value: "3"
	},{
		label: translate("Abril"),
		value: "4"
	},{
		label: translate("Maio"),
		value: "5"
	},{
		label: translate("Junho"),
		value: "6"
	},{
		label: translate("Julho"),
		value: "7"
	},{
		label: translate("Agosto"),
		value: "8"
	},{
		label: translate("Setembro"),
		value: "9"
	},{
		label: translate("Outubro"),
		value: "10"
	},{
		label: translate("Novembro"),
		value: "11"
	},{
		label: translate("Dezembro"),
		value: "12"
	}];

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const [showCoinLov, setShowCoinLov] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		control: controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange, 
		unregister: unRegisterForm 
	} = useForm();

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setIsAdd(true);
		setBudgetPlanSelected({
			ofa001Utilizamoeda: "N",
			oftbp001: [{
				ofp001Id: 0,
				ofp001Permissao: 1,
				ofp001Efa005Id: user.efa005Id,
				ofp001Efa005Nome: user.efa005Nome
			}]
		} as IOftba001Form);
		setShowCoinLov(false);	
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (card: ISidebarCardItem) => {
		resetAddOrChange(); 
		setIsAdd(false);
		getBudgetPlan(card.id);
	};

	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit: handleSubmitDelete, 
		register: registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	const actionButtonDelete = (card: ISidebarCardItem) => {
		resetDelete();
		setBudgetPlanDelete(card);
		modalDelete.current?.openModal();
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			const dataForm = data;		
			dataForm.ofa001Template = data.ofa001Template ? "S" : "N";
			dataForm.ofa001Rascunho = data.ofa001Rascunho ? "S" : "N";
			dataForm.ofa001Utilizamoeda = data.ofa001Utilizamoeda ? "S" : "N";

			if(data.oftbp001 && data.oftbp001.length > 0){
				dataForm.oftbp001 = data.oftbp001.map((ofp001: any) => {
					if(typeof ofp001.ofp001Efa005Id === "object")
						return {
							...ofp001,
							ofp001Acessocv: ofp001.ofp001Acessocv ? "S" : "N",
							ofp001Acessode: ofp001.ofp001Acessode ? "S" : "N",
							ofp001Acessodo: ofp001.ofp001Acessodo ? "S" : "N",
							ofp001Acessodv: ofp001.ofp001Acessodv ? "S" : "N",
							ofp001Acessoou: ofp001.ofp001Acessoou ? "S" : "N",
							ofp001Acessore: ofp001.ofp001Acessore ? "S" : "N",
							ofp001Permissao: Number(ofp001.ofp001Permissao),
							ofp001Efa005Id: ofp001.ofp001Efa005Id.value
						};
					if(typeof ofp001.ofp001Efa004Id === "object")
						return {
							...ofp001,
							ofp001Acessocv: ofp001.ofp001Acessocv ? "S" : "N",
							ofp001Acessode: ofp001.ofp001Acessode ? "S" : "N",
							ofp001Acessodo: ofp001.ofp001Acessodo ? "S" : "N",
							ofp001Acessodv: ofp001.ofp001Acessodv ? "S" : "N",
							ofp001Acessoou: ofp001.ofp001Acessoou ? "S" : "N",
							ofp001Acessore: ofp001.ofp001Acessore ? "S" : "N",
							ofp001Permissao: Number(ofp001.ofp001Permissao),
							ofp001Efa004Id: ofp001.ofp001Efa004Id.value
						};
					return {
						...ofp001,
						ofp001Acessocv: ofp001.ofp001Acessocv ? "S" : "N",
						ofp001Acessode: ofp001.ofp001Acessode ? "S" : "N",
						ofp001Acessodo: ofp001.ofp001Acessodo ? "S" : "N",
						ofp001Acessodv: ofp001.ofp001Acessodv ? "S" : "N",
						ofp001Acessoou: ofp001.ofp001Acessoou ? "S" : "N",
						ofp001Acessore: ofp001.ofp001Acessore ? "S" : "N"
					};
				});

				// // verifica se tem permissão de Visializar e Editar
				// if(!dataForm.oftbp001.filter((x:any) => Number(x.ofp001Permissao) === Number(simplePermissionType.VIEW_AND_EDIT)).length){
				// 	modalAddOrChange.current?.resetLoadingButton();
				// 	addToast({
				// 		color: "yellow",
				// 		description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
				// 		title: translate("Atenção")
				// 	});
				// 	return;
				// }
				
				if(data.oftbp001 && data.oftbp001.length > 0){
					dataForm.oftbp001 = data.oftbp001.filter((dt: any) => dt.ofp001Efa005Id !== 0);
					dataForm.oftbp001 = data.oftbp001.filter((dt: any) => dt.ofp001Efa004Id !== 0);
				}
			}

			// Seta o primeiro dia do ano
			if(dataForm.ofa001Dtinicio instanceof Date){		
				const ofa001DtinicioYear = new Date(dataForm.ofa001Dtinicio).getFullYear();		
				dataForm.ofa001Dtinicio = new Date(ofa001DtinicioYear, 0, 1);
			}

			// Seta o ultimo dia do ano
			if(dataForm.ofa001Dtfim instanceof Date){
				const ofa001DtfimYear = new Date(dataForm.ofa001Dtfim).getFullYear();
				dataForm.ofa001Dtfim = new Date(ofa001DtfimYear, 11, 31);
			}

			await addAndChangeSchema(translate).validate({...dataForm}, {
				abortEarly: false
			});

			dataForm.ofa001Moa001Id = data.ofa001Moa001Id === "" ? null : Number(data.ofa001Moa001Id);

			if(isAdd){
				const result = await RCCServicesApi.postRequest({
					method: "FinancialManagement/InsertBudgetPlan",
					body: {
						...dataForm,
						ofa001Id: 0		
					}
				});

				onCallbackAdd(result.data);
			}else{
				const result = await RCCServicesApi.putRequest({
					method: "FinancialManagement/EditBudgetPlan",
					body: {
						...dataForm
					}
				});

				onCallbackUpdate(result.data);
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Plano orcamentário adicionado com sucesso") : translate("Plano orcamentário alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar plano orçamentário") : translate("Erro ao alterar plano orçamentário"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();
			
			await deleteSchema(translate).validate({...data, "ofa001NomeConfirmation": budgetPlanDelete?.title},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "FinancialManagement/DeleteBudgetPlan",
				urlParameters: {
					id: budgetPlanDelete?.id,
					title: budgetPlanDelete?.title
				}
			});

			addToast({
				color: "green",
				description: translate("Plano orçamentário excluído com sucesso" ),
				title: translate("Sucesso")
			});

			window.setTimeout(() => {
				addToast({
					color: "blue",
					description: translate("Você pode restaurar/verificar os itens excluidos na tela da lixeira"),
					title: translate("Atenção")
				});
			}, 2000);

			onCallbackDelete(budgetPlanDelete?.id || 0);

			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir plano orçamentário"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	async function getBudgetPlan(idBudgetPlan: number){
		try{
			setLoading(true);
			const result = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectBudgetPlan",
				urlParameters: {id: idBudgetPlan}
			});
			setBudgetPlanSelected(result.data);
			setShowCoinLov(result.data.ofa001Utilizamoeda === "S");
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar plano orçamentário"), addToast);
		}
	}

	return (
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão do plano orçamentário")}
				description= {`${translate("Deseja realmente excluir o plano orçamentário")}: ${budgetPlanDelete?.title}?`}
				onSubmit = {handleSubmitDelete(submitDelete)}
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="ofa001Nome"
						label={translate("Digite o nome do plano orçamentário para excluí-lo")}
						maxLength={200}
						error={errorsDelete.ofa001Nome}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar plano orçamentário") : translate("Alterar plano orçamentário"),
					subtitle: !isAdd ? `${translate("Plano orçamentário")}: ${budgetPlanSelected?.ofa001Nome}` : "",
					icon: {
						name: "dollar"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={`${ModalSide} min-width: 55vw;`}
			>
				<Tabs
					name="tabMapa"
					tabInit={0}
					options={[
						{
							label: translate("Geral"),
							content: <RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								{!isAdd && budgetPlanSelected && (
									<RCCGrid
										container
										item
										alignItems="center"
										justifyContent="flex-end"
									>
										<Audit data={budgetPlanSelected} type="pop"/>
									</RCCGrid>
								)}
								<input 
									type="text"
									value={budgetPlanSelected?.ofa001Id}
									{...registerAddOrChange("ofa001Id")} 
									style={{ display: "none" }} 
									readOnly
								/>
								<RCCGrid
									container
									item
								>
									<RCCInput 
										register={registerAddOrChange}
										name="ofa001Nome"
										label={translate("Nome")}
										maxLength={100}
										defaultValue={budgetPlanSelected?.ofa001Nome}
										error={errorsAddOrChange.ofa001Nome}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<DateTimePickerRange
										control={controlAddOrChange}
										type="datePicker"
										label={translate("Período")}
										labelSeparator={translate("Até")}
										nameInit="ofa001Dtinicio"
										nameEnd="ofa001Dtfim"
										defaultValueInit={budgetPlanSelected?.ofa001Dtinicio ? budgetPlanSelected.ofa001Dtinicio : new Date(new Date().getFullYear(), 0, 1)}
										defaultValueEnd={budgetPlanSelected?.ofa001Dtfim ? budgetPlanSelected.ofa001Dtfim : new Date(new Date().getFullYear(), 11, 31)}
										error={errorsAddOrChange.ofa001Dtinicio || errorsAddOrChange.ofa001Dtfim}
										required
										props={{
											datePicker: {
												format: "yyyy",
												views: ["year"]
											}
										}}
									/>							
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<DateTimePicker
										name="ofa001Dtplanejado"
										type="datePicker"
										label={translate("Data edição planejado")}
										control={controlAddOrChange}
										defaultValue={budgetPlanSelected?.ofa001Dtplanejado ? budgetPlanSelected.ofa001Dtplanejado : new Date(new Date().getFullYear(), 0, 1)}
										error={errorsAddOrChange.ofa001Dtplanejado}
										required
										props={{
											datePicker: {
												format: "MM/yyyy",
												views: ["month", "year"]
											}
										}}
									/>						
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<DateTimePicker
										name="ofa001Dtrealizado"
										type="datePicker"
										label={translate("Data edição realizado")}
										control={controlAddOrChange}
										defaultValue={budgetPlanSelected?.ofa001Dtrealizado ? budgetPlanSelected?.ofa001Dtrealizado : new Date(new Date().getFullYear(), 0, 1)}
										error={errorsAddOrChange.ofa001Dtrealizado}
										required
										props={{
											datePicker: {
												format: "MM/yyyy",
												views: ["month", "year"]
											}
										}}
									/>						
								</RCCGrid>
								<RCCGrid
									container
									item
								>
									<Select
										name="ofa001Mesinicio"
										label={translate("Mês de inicio")}
										control={controlAddOrChange}
										options={monthOptions}	
										defaultValue={budgetPlanSelected?.ofa001Mesinicio ? 
											budgetPlanSelected?.ofa001Mesinicio.toString() : 
											monthOptions[0].value
										}
										error={errorsAddOrChange.ofa001Mesinicio}
									/>						
								</RCCGrid>
								<RCCGrid 
									container
									item
									alignItems="center"
								>
									<RCCGrid container item xs={6}>
										<RCCSwitch 
											name="ofa001Rascunho" 
											label={translate("Rascunho")} 
											register={registerAddOrChange} 
											defaultChecked={budgetPlanSelected?.ofa001Rascunho === "S"}
										/>
									</RCCGrid>
									{company.efa002Id === 1 && (
										<RCCGrid container item xs={6}>
											<RCCSwitch 
												name="ofa001Template" 
												label="Template" 
												register={registerAddOrChange} 
												defaultChecked={budgetPlanSelected?.ofa001Template === "S"}
											/>
										</RCCGrid>
									)} 
								</RCCGrid>
								<RCCGrid 
									container
									item
									alignItems="center"
									style={{
										height: "60px"
									}}
								>
									<RCCGrid container item xs={6}>
										<RCCSwitch 
											name="ofa001Utilizamoeda" 
											label={translate("Utiliza conversão de moeda")} 
											register={registerAddOrChange} 
											defaultChecked={budgetPlanSelected?.ofa001Utilizamoeda === "S"}
											onChangeCapture={(e:any) => setShowCoinLov(e.target.checked)}
										/>
									</RCCGrid>
									{showCoinLov && (
										<RCCGrid container item xs={6}>
											<LovCoins 
												name="ofa001Moa001Id"
												label={translate("Moeda origem")}
												control={controlAddOrChange}
												defaultValue={budgetPlanSelected?.ofa001Moa001Id?.toString()}
												error={errorsAddOrChange.ofa001Moa001Id}
												required
											/>
										</RCCGrid>
									)} 
								</RCCGrid>
							</RCCGrid>
						},
						{
							label: translate("Permissões"),
							content: 
							<Permissions
								register={registerAddOrChange}
								unregister={unRegisterForm}
								control={controlAddOrChange}
								error={errorsAddOrChange}
								clearErrors={clearErrorsAddOrChange}
								permissionsNames= {{
									nameObject: "oftbp001",
									nameOfID: "ofp001Id",
									nameOfSelectUserDescription: "ofp001Efa005Nome",
									nameOfSelectGroupDescription: "ofp001Efa004Nome",
									nameOfSelectUser: "ofp001Efa005Id",
									nameOfSelectGroup: "ofp001Efa004Id",
									nameOfSelectPermission: "ofp001Permissao",
									defaultValue: budgetPlanSelected?.oftbp001
								}}
								customPermissionOptions={[{
									value: "1",
									label: translate("Controle total")
								},
								{
									value: "2",
									label: translate("Acesso orçamentos")
								},
								{
									value: "3",
									label: translate("Acesso análises")
								},
								{
									value: "4",
									label: translate("Acesso orçamentos e análises")
								}]}
								additionalFields={(register: any, control: any, error: any, prefixNameInputs: string, permission: any, updatePermissions: (value: any, key: string) => void) => {
									if(permission.permission === "1" || permission.permission === "3")
										return null;
									return (<AdditionalPermissions 
										permission={permission}
										prefixNameInputs={prefixNameInputs}
										register={register}
										updatePermissions={updatePermissions}
									/>);
								}}
							/>
						}
					]}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(ModalsBudgetPlanAddChangeDelete);
